import { api } from '@pwa/lib/hono-client';
import { queryClient } from '@pwa/lib/query-client';
import { sessionQueryOptions } from '@pwa/queries/auth/session';
import { Button } from '@repo/ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/card';
import { Skeleton } from '@repo/ui/components/skeleton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Loader2, MailPlus, RefreshCcw, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const tokenSchema = z.object({
	token: z.string().min(1, 'Token is required'),
});

export const Route = createFileRoute('/invites/accept')({
	validateSearch: tokenSchema,
	component: RouteComponent,
});

export function getAcceptedInviteNavigation(body: unknown) {
	if (typeof body === 'object' && body !== null) {
		if (
			'member' in body &&
			typeof body.member === 'object' &&
			body.member !== null &&
			'organizationId' in body.member &&
			typeof body.member.organizationId === 'string' &&
			body.member.organizationId.length > 0
		) {
			return { to: '/org/$orgId' as const, params: { orgId: body.member.organizationId } };
		}

		if (
			'organization' in body &&
			typeof body.organization === 'object' &&
			body.organization !== null &&
			'id' in body.organization &&
			typeof body.organization.id === 'string' &&
			body.organization.id.length > 0
		) {
			return { to: '/org/$orgId' as const, params: { orgId: body.organization.id } };
		}
	}

	return { to: '/' as const };
}

export async function acceptInvite(token: string) {
	const response = await api.invites.accept.$post({
		json: { token },
	});
	const body = await response.json();

	if (!response.ok) {
		throw new Error('error' in body ? String(body.error) : 'Failed to accept invite');
	}

	return body;
}

function RouteComponent() {
	const { token } = Route.useSearch();
	const navigate = useNavigate();
	const [hasAccepted, setHasAccepted] = useState(false);

	useEffect(() => {
		document.title = 'Accept invite · Scout';
	}, []);

	const { data: sessionData, isLoading: isSessionLoading } = useQuery(sessionQueryOptions);

	const acceptMutation = useMutation({
		mutationFn: () => acceptInvite(token),
		onSuccess: async (body) => {
			setHasAccepted(true);
			queryClient.clear();
			await navigate(getAcceptedInviteNavigation(body));
		},
	});

	const loginRedirect = `/invites/accept?token=${encodeURIComponent(token)}`;

	if (isSessionLoading) {
		return (
			<div className='flex min-h-dvh flex-col items-center justify-center p-4'>
				<Card
					className='w-full max-w-sm animate-in fade-in slide-in-from-bottom-1 duration-280 ease-out fill-mode-both'
					aria-busy='true'
				>
					<CardHeader className='text-center'>
						<Skeleton className='mx-auto mb-2 size-10 rounded-full' />
						<Skeleton className='mx-auto h-5 w-40' />
						<Skeleton className='mx-auto mt-2 h-4 w-56' />
					</CardHeader>
					<CardContent className='space-y-3'>
						<Skeleton className='h-9 w-full' />
						<Skeleton className='h-9 w-full' />
					</CardContent>
				</Card>
			</div>
		);
	}

	if (!sessionData?.data) {
		return (
			<div className='flex min-h-dvh flex-col items-center justify-center p-4'>
				<Card className='w-full max-w-sm animate-in fade-in slide-in-from-bottom-1 duration-280 ease-out fill-mode-both'>
					<CardHeader className='text-center'>
						<div
							className='mx-auto mb-2 grid size-12 place-items-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20'
							aria-hidden='true'
						>
							<MailPlus className='size-5' />
						</div>
						<CardTitle>Sign in or create an account</CardTitle>
						<CardDescription>Sign in first, then accept your invite.</CardDescription>
					</CardHeader>
					<CardContent className='space-y-3'>
						<Button
							className='w-full'
							onClick={() => {
								window.location.href = `/login?redirect=${encodeURIComponent(loginRedirect)}`;
							}}
						>
							Sign in to accept
						</Button>
						<Button
							variant='outline'
							className='w-full'
							onClick={() => {
								window.location.href = `/login?mode=signup&redirect=${encodeURIComponent(loginRedirect)}`;
							}}
						>
							Create an account
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className='flex min-h-dvh flex-col items-center justify-center p-4'>
			{hasAccepted ? (
				<Card className='w-full max-w-sm animate-in fade-in slide-in-from-bottom-1 duration-280 ease-out fill-mode-both'>
					<CardHeader className='items-center text-center'>
						<div className='mb-2 grid size-12 place-items-center rounded-full bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/20 animate-in fade-in zoom-in-50 duration-500 ease-out fill-mode-both dark:text-emerald-400'>
							<CheckCircle2 className='size-6' aria-hidden='true' />
						</div>
						<CardTitle>You&apos;re in</CardTitle>
						<CardDescription>Invite accepted. Taking you to your workspace…</CardDescription>
					</CardHeader>
				</Card>
			) : (
				<Card className='w-full max-w-sm animate-in fade-in slide-in-from-bottom-1 duration-280 ease-out fill-mode-both'>
					<CardHeader className='text-center'>
						<CardTitle>You&apos;re invited!</CardTitle>
						<CardDescription>You&apos;ve been invited to join a Scout team.</CardDescription>
					</CardHeader>
					<CardContent className='space-y-4'>
						{!acceptMutation.isPending && !acceptMutation.isError && (
							<Button className='w-full' onClick={() => acceptMutation.mutate()}>
								Accept invite
							</Button>
						)}
						{acceptMutation.isPending && (
							<Button className='w-full' disabled>
								<Loader2 className='size-4 animate-spin' aria-hidden='true' />
								Joining…
							</Button>
						)}
						{acceptMutation.isError && (
							<div
								role='alert'
								className='animate-in fade-in slide-in-from-bottom-1 duration-200 ease-out fill-mode-both space-y-3'
							>
								<div className='flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
									<XCircle className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
									<div className='space-y-0.5'>
										<p className='font-medium'>Couldn&apos;t accept invite</p>
										<p className='text-pretty text-destructive/90'>{acceptMutation.error.message}</p>
									</div>
								</div>
								<Button variant='outline' className='w-full' onClick={() => acceptMutation.reset()}>
									<RefreshCcw className='size-4' aria-hidden='true' />
									Try again
								</Button>
							</div>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
